import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { 
  Plus, Trash2, Upload, X, Image, FolderPlus, 
  Loader2, ChevronLeft, Images, Check, Edit2, Save, Filter
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Category options
const CATEGORIES = [
  'Infrastructure',
  'Pharma',
  'Agriculture',
  'Mining',
  'Logistics',
  'Energy',
  'Manufacturing',
  'Technology',
  'ClimateTech',
  'Trade',
  'Environment',
  'Solar',
  'Water'
];

export const AdminPhotoManager = ({ onBack }) => {
  const [topics, setTopics] = useState([]);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [showNewTopicModal, setShowNewTopicModal] = useState(false);
  const [showEditTopicModal, setShowEditTopicModal] = useState(false);
  const [editingTopic, setEditingTopic] = useState(null);
  const [newTopicName, setNewTopicName] = useState('');
  const [newTopicDescription, setNewTopicDescription] = useState('');
  const [newTopicCategory, setNewTopicCategory] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchTopics();
  }, []);

  useEffect(() => {
    if (selectedTopic) {
      fetchPhotos(selectedTopic.id);
    }
  }, [selectedTopic]);

  const fetchTopics = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/gallery/topics`);
      setTopics(response.data);
    } catch (error) {
      console.error('Error fetching topics:', error);
      toast.error('Failed to load topics');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPhotos = async (topicId) => {
    try {
      const response = await axios.get(`${API_URL}/api/gallery/photos/${topicId}`);
      setPhotos(response.data);
    } catch (error) {
      console.error('Error fetching photos:', error);
    }
  };

  const createTopic = async () => {
    if (!newTopicName.trim()) {
      toast.error('Please enter a topic name');
      return;
    }

    try {
      const response = await axios.post(`${API_URL}/api/gallery/topics`, {
        name: newTopicName,
        description: newTopicDescription,
        category: newTopicCategory || null
      });
      setTopics([response.data, ...topics]);
      setShowNewTopicModal(false);
      setNewTopicName('');
      setNewTopicDescription('');
      setNewTopicCategory('');
      toast.success('Topic created!');
    } catch (error) {
      console.error('Error creating topic:', error);
      toast.error('Failed to create topic');
    }
  };

  const deleteTopic = async (topicId) => {
    if (!window.confirm('Delete this topic and all its photos?')) return;

    try {
      await axios.delete(`${API_URL}/api/gallery/topics/${topicId}`);
      setTopics(topics.filter(t => t.id !== topicId));
      if (selectedTopic?.id === topicId) {
        setSelectedTopic(null);
        setPhotos([]);
      }
      toast.success('Topic deleted');
    } catch (error) {
      console.error('Error deleting topic:', error);
      toast.error('Failed to delete topic');
    }
  };

  const openEditModal = (topic, e) => {
    e.stopPropagation();
    setEditingTopic(topic);
    setNewTopicName(topic.name);
    setNewTopicDescription(topic.description || '');
    setNewTopicCategory(topic.category || '');
    setShowEditTopicModal(true);
  };

  const updateTopic = async () => {
    if (!newTopicName.trim() || !editingTopic) {
      toast.error('Please enter a topic name');
      return;
    }

    try {
      const response = await axios.put(`${API_URL}/api/gallery/topics/${editingTopic.id}`, {
        name: newTopicName,
        description: newTopicDescription,
        category: newTopicCategory || null
      });
      setTopics(topics.map(t => t.id === editingTopic.id ? { ...t, name: newTopicName, description: newTopicDescription, category: newTopicCategory } : t));
      if (selectedTopic?.id === editingTopic.id) {
        setSelectedTopic({ ...selectedTopic, name: newTopicName, description: newTopicDescription, category: newTopicCategory });
      }
      setShowEditTopicModal(false);
      setEditingTopic(null);
      setNewTopicName('');
      setNewTopicDescription('');
      setNewTopicCategory('');
      toast.success('Topic updated!');
    } catch (error) {
      console.error('Error updating topic:', error);
      toast.error('Failed to update topic');
    }
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length || !selectedTopic) return;

    setIsUploading(true);
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });

    try {
      const response = await axios.post(
        `${API_URL}/api/gallery/upload/${selectedTopic.id}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      setPhotos([...response.data.photos, ...photos]);
      
      // Update topic photo count
      setTopics(topics.map(t => 
        t.id === selectedTopic.id 
          ? { ...t, photo_count: t.photo_count + response.data.uploaded }
          : t
      ));
      
      toast.success(`${response.data.uploaded} photo(s) uploaded!`);
    } catch (error) {
      console.error('Error uploading photos:', error);
      toast.error('Failed to upload photos');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const deletePhoto = async (photoId) => {
    try {
      await axios.delete(`${API_URL}/api/gallery/photos/${photoId}`);
      setPhotos(photos.filter(p => p.id !== photoId));
      
      // Update topic photo count
      setTopics(topics.map(t => 
        t.id === selectedTopic.id 
          ? { ...t, photo_count: Math.max(0, t.photo_count - 1) }
          : t
      ));
      
      toast.success('Photo deleted');
    } catch (error) {
      console.error('Error deleting photo:', error);
      toast.error('Failed to delete photo');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 size={32} className="animate-spin text-[#D4AF37]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {selectedTopic && (
            <button
              onClick={() => { setSelectedTopic(null); setPhotos([]); }}
              className="p-2 rounded-lg bg-white/5 text-white/60 hover:bg-white/10 hover:text-white"
            >
              <ChevronLeft size={20} />
            </button>
          )}
          <h2 className="text-xl font-bold text-white">
            {selectedTopic ? selectedTopic.name : 'Photo Gallery'}
          </h2>
        </div>
        
        {!selectedTopic ? (
          <button
            onClick={() => setShowNewTopicModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-[#0A0A0A] font-semibold rounded-lg hover:bg-[#E5C048] transition-colors"
          >
            <FolderPlus size={18} />
            New Topic
          </button>
        ) : (
          <div>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-[#0A0A0A] font-semibold rounded-lg hover:bg-[#E5C048] transition-colors disabled:opacity-50"
            >
              {isUploading ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload size={18} />
                  Upload Photos
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Topics List or Photos Grid */}
      {!selectedTopic ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {topics.length === 0 ? (
            <div className="col-span-full text-center py-16 bg-[#111] rounded-2xl border border-white/10">
              <Images size={48} className="mx-auto text-white/20 mb-4" />
              <p className="text-white/50 mb-4">No topics yet</p>
              <button
                onClick={() => setShowNewTopicModal(true)}
                className="px-4 py-2 bg-[#D4AF37] text-[#0A0A0A] font-semibold rounded-lg"
              >
                Create First Topic
              </button>
            </div>
          ) : (
            topics.map(topic => (
              <motion.div
                key={topic.id}
                whileHover={{ scale: 1.02 }}
                className="bg-[#111] border border-white/10 rounded-xl overflow-hidden cursor-pointer group"
                onClick={() => setSelectedTopic(topic)}
              >
                <div className="aspect-video bg-white/5 relative">
                  {topic.cover_image ? (
                    <img
                      src={`${API_URL}${topic.cover_image}`}
                      alt={topic.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Images size={40} className="text-white/20" />
                    </div>
                  )}
                  <div className="absolute top-2 right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={(e) => openEditModal(topic, e)}
                      className="p-2 rounded-lg bg-[#D4AF37]/80 text-[#0A0A0A]"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); deleteTopic(topic.id); }}
                      className="p-2 rounded-lg bg-red-500/80 text-white"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-white">{topic.name}</h3>
                    {topic.category && (
                      <span className="px-2 py-0.5 text-xs bg-[#D4AF37]/20 text-[#D4AF37] rounded-full">
                        {topic.category}
                      </span>
                    )}
                  </div>
                  {topic.description && (
                    <p className="text-sm text-white/50 mt-1 line-clamp-1">{topic.description}</p>
                  )}
                  <p className="text-xs text-[#D4AF37] mt-2">{topic.photo_count} photos</p>
                </div>
              </motion.div>
            ))
          )}
        </div>
      ) : (
        <div>
          {photos.length === 0 ? (
            <div className="text-center py-16 bg-[#111] rounded-2xl border border-white/10">
              <Image size={48} className="mx-auto text-white/20 mb-4" />
              <p className="text-white/50 mb-4">No photos in this topic</p>
              <button
                onClick={() => fileInputRef.current?.click()}
                className="px-4 py-2 bg-[#D4AF37] text-[#0A0A0A] font-semibold rounded-lg"
              >
                Upload First Photo
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {photos.map(photo => (
                <div
                  key={photo.id}
                  className="aspect-square rounded-xl overflow-hidden relative group"
                >
                  <img
                    src={`${API_URL}${photo.url}`}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                  <button
                    onClick={() => deletePhoto(photo.id)}
                    className="absolute top-2 right-2 p-2 rounded-lg bg-red-500/80 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* New Topic Modal */}
      <AnimatePresence>
        {showNewTopicModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
            onClick={() => setShowNewTopicModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="bg-[#111] border border-white/10 rounded-2xl p-6 w-full max-w-md"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold text-white">Create New Topic</h3>
                <button
                  onClick={() => setShowNewTopicModal(false)}
                  className="p-1 rounded-lg hover:bg-white/10 text-white/50"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-white/60 mb-2">Topic Name *</label>
                  <input
                    type="text"
                    value={newTopicName}
                    onChange={(e) => setNewTopicName(e.target.value)}
                    placeholder="e.g., Nigeria Business Summit 2026"
                    className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder:text-white/30 focus:outline-none focus:border-[#D4AF37]"
                  />
                </div>
                <div>
                  <label className="block text-sm text-white/60 mb-2">Category</label>
                  <select
                    value={newTopicCategory}
                    onChange={(e) => setNewTopicCategory(e.target.value)}
                    className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white focus:outline-none focus:border-[#D4AF37] appearance-none cursor-pointer"
                  >
                    <option value="" className="bg-[#1a1a1a]">Select a category...</option>
                    {CATEGORIES.map(cat => (
                      <option key={cat} value={cat} className="bg-[#1a1a1a]">{cat}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-white/60 mb-2">Description (Optional)</label>
                  <textarea
                    value={newTopicDescription}
                    onChange={(e) => setNewTopicDescription(e.target.value)}
                    placeholder="Brief description of this photo collection..."
                    rows={3}
                    className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder:text-white/30 focus:outline-none focus:border-[#D4AF37] resize-none"
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowNewTopicModal(false)}
                  className="flex-1 py-3 border border-white/20 rounded-lg text-white/60 hover:bg-white/5"
                >
                  Cancel
                </button>
                <button
                  onClick={createTopic}
                  className="flex-1 py-3 bg-[#D4AF37] text-[#0A0A0A] font-semibold rounded-lg hover:bg-[#E5C048]"
                >
                  Create Topic
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit Topic Modal */}
      <AnimatePresence>
        {showEditTopicModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
            onClick={() => setShowEditTopicModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="bg-[#111] border border-white/10 rounded-2xl p-6 w-full max-w-md"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold text-white">Edit Topic</h3>
                <button
                  onClick={() => setShowEditTopicModal(false)}
                  className="p-1 rounded-lg hover:bg-white/10 text-white/50"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-white/60 mb-2">Topic Name *</label>
                  <input
                    type="text"
                    value={newTopicName}
                    onChange={(e) => setNewTopicName(e.target.value)}
                    placeholder="e.g., Nigeria Business Summit 2026"
                    className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder:text-white/30 focus:outline-none focus:border-[#D4AF37]"
                  />
                </div>
                <div>
                  <label className="block text-sm text-white/60 mb-2">Category</label>
                  <select
                    value={newTopicCategory}
                    onChange={(e) => setNewTopicCategory(e.target.value)}
                    className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white focus:outline-none focus:border-[#D4AF37] appearance-none cursor-pointer"
                  >
                    <option value="" className="bg-[#1a1a1a]">Select a category...</option>
                    {CATEGORIES.map(cat => (
                      <option key={cat} value={cat} className="bg-[#1a1a1a]">{cat}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-white/60 mb-2">Description (Optional)</label>
                  <textarea
                    value={newTopicDescription}
                    onChange={(e) => setNewTopicDescription(e.target.value)}
                    placeholder="Brief description of this photo collection..."
                    rows={3}
                    className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder:text-white/30 focus:outline-none focus:border-[#D4AF37] resize-none"
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowEditTopicModal(false)}
                  className="flex-1 py-3 border border-white/20 rounded-lg text-white/60 hover:bg-white/5"
                >
                  Cancel
                </button>
                <button
                  onClick={updateTopic}
                  className="flex-1 py-3 bg-[#D4AF37] text-[#0A0A0A] font-semibold rounded-lg hover:bg-[#E5C048] flex items-center justify-center gap-2"
                >
                  <Save size={18} />
                  Save Changes
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
